/**
 * Integration Logs Page
 *
 * Admin page to view integration onboarding logs for a specific user.
 * Displays user information, integration metadata, and logs from CloudWatch.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconUser, IconBuilding } from "@tabler/icons-react";
import { fetchIntegrationLogs } from "../api";
import { LogsDisplay } from "../TimestepDetail/components/LogsDisplay";
import type { IntegrationLogsData } from "../types";
import { AppResources } from "@/constants";

export const IntegrationLogs: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [data, setData] = useState<IntegrationLogsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!userId) {
      setError("User ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const logsData = await fetchIntegrationLogs(userId);
      setData(logsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [userId]);

  const handleRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <>
        <style>
          {`
            @keyframes fadeInOut {
              0%, 100% {
                opacity: 0.3;
              }
              50% {
                opacity: 1;
              }
            }

            @keyframes gentleFloat {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-8px);
              }
            }

            .wizzy-loading {
              animation: fadeInOut 2s ease-in-out infinite, gentleFloat 3s ease-in-out infinite;
            }
          `}
        </style>
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 9999,
          }}
        >
          <Stack align="center" gap="md">
            <img
              src={AppResources.wizzyWithGlases}
              alt="Loading..."
              className="wizzy-loading"
              style={{
                width: "120px",
                height: "120px",
              }}
            />
            <Text c="dimmed" size="sm">
              Loading integration logs...
            </Text>
          </Stack>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Data"
          color="red"
          variant="filled"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No Data Available"
          color="yellow"
        >
          No integration logs found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Page Title */}
        <div>
          <Title order={1} mb="xs">
            Integration Onboarding Logs
          </Title>
          <Text c="dimmed" size="sm">
            View integration onboarding logs and debugging information
          </Text>
        </div>

        {/* User Information Card */}
        <Paper withBorder p="lg" radius="md">
          <Stack gap="md">
            <Group gap="xs">
              <IconUser size={20} />
              <Text fw={600} size="lg">
                User Information
              </Text>
            </Group>

            <Group gap="xl">
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Name
                </Text>
                <Text size="sm">{data.userInfo.name}</Text>
              </Box>

              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Email
                </Text>
                <Text size="sm">{data.userInfo.email}</Text>
              </Box>

              {data.userInfo.businessName && (
                <Box>
                  <Group gap="xs">
                    <IconBuilding size={16} />
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Business
                    </Text>
                  </Group>
                  <Text size="sm">{data.userInfo.businessName}</Text>
                </Box>
              )}

              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  User ID
                </Text>
                <Text size="sm" ff="monospace">
                  {data.userInfo.id}
                </Text>
              </Box>
            </Group>
          </Stack>
        </Paper>

        {/* Integration Metadata Card */}
        {data.integrations.length > 0 && (
          <Paper withBorder p="lg" radius="md">
            <Stack gap="md">
              <Group gap="xs">
                <Text fw={600} size="lg">
                  Integration Attempts
                </Text>
                <Badge size="lg" variant="light">
                  {data.integrations.length}
                </Badge>
              </Group>

              <Stack gap="xs">
                {data.integrations.map((integration, index) => (
                  <Paper key={index} withBorder p="sm" radius="sm">
                    <Group justify="space-between" wrap="nowrap">
                      <Box>
                        <Text fw={600} size="sm">
                          {integration.integrationType}
                        </Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {integration.logStreamName}
                        </Text>
                      </Box>
                      <Group gap="md">
                        <Box>
                          <Text size="xs" c="dimmed">
                            Last Event
                          </Text>
                          <Text size="xs">
                            {new Date(
                              integration.lastEventTime
                            ).toLocaleString()}
                          </Text>
                        </Box>
                        <Box>
                          <Text size="xs" c="dimmed">
                            Size
                          </Text>
                          <Text size="xs">
                            {(integration.storedBytes / 1024).toFixed(2)} KB
                          </Text>
                        </Box>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Logs Display */}
        {data.logs.length > 0 ? (
          <Paper withBorder radius="md">
            <LogsDisplay
              logs={data.logs}
              logSourceType="cloudwatch"
              onRefresh={handleRefresh}
            />
          </Paper>
        ) : (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="No Logs Available"
            color="blue"
          >
            {data.integrations.length > 0
              ? "No logs found for the most recent integration attempt. Logs may have been cleaned up or the integration did not produce any logs."
              : "No integration attempts found for this user."}
          </Alert>
        )}

        {/* Selected Log Stream Info */}
        {data.selectedLogStream && (
          <Paper withBorder p="sm" radius="md" bg="gray.0">
            <Text size="xs" c="dimmed">
              Showing logs from:{" "}
              <Text component="span" ff="monospace" fw={600}>
                {data.selectedLogStream}
              </Text>
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};
